import React, { useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from '@/components/ui/toast';

const LIST_REFERRAL_UNACCEPTABLE_CHARGES = gql`
  query ReferralUnacceptableCharges {
    referralUnacceptableCharges {
      id
      charge
      note
      active
    }
  }
`;

const ADD_REFERRAL_UNACCEPTABLE_CHARGE = gql`
  mutation AddReferralUnacceptableCharge($charge: String!, $note: String) {
    addReferralUnacceptableCharge(charge: $charge, note: $note) {
      id
      charge
      note
      active
    }
  }
`;

const REMOVE_REFERRAL_UNACCEPTABLE_CHARGE = gql`
  mutation RemoveReferralUnacceptableCharge($id: Int!) {
    removeReferralUnacceptableCharge(id: $id) {
      message
      responseCode
      responseID
      extra
    }
  }
`;

type ChargeRow = {
  id: number;
  charge: string;
  note?: string | null;
  active?: boolean | null;
};

export const ReferralUnacceptableChargesPage: React.FC = () => {
  const [chargeInput, setChargeInput] = useState('');
  const [noteInput, setNoteInput] = useState('');

  const { data, loading, refetch } = useQuery<{ referralUnacceptableCharges: ChargeRow[] }>(
    LIST_REFERRAL_UNACCEPTABLE_CHARGES,
    {
      fetchPolicy: 'cache-and-network',
    }
  );

  const [addCharge, { loading: adding }] = useMutation(ADD_REFERRAL_UNACCEPTABLE_CHARGE);
  const [removeCharge, { loading: removing }] = useMutation(REMOVE_REFERRAL_UNACCEPTABLE_CHARGE);

  const rows = useMemo(() => {
    const values = data?.referralUnacceptableCharges;
    return Array.isArray(values) ? values : [];
  }, [data]);

  const handleAdd = async () => {
    const charge = chargeInput.trim();
    const note = noteInput.trim();

    if (!charge) {
      toast.error('Charge is required');
      return;
    }

    try {
      await addCharge({
        variables: {
          charge,
          note: note || null,
        },
      });

      setChargeInput('');
      setNoteInput('');
      await refetch();
      toast.success('Unacceptable charge saved');
    } catch (error: any) {
      toast.error(error?.message || 'Unable to save charge');
    }
  };

  const handleRemove = async (id: number) => {
    try {
      await removeCharge({ variables: { id } });
      await refetch();
      toast.success('Unacceptable charge removed');
    } catch (error: any) {
      toast.error(error?.message || 'Unable to remove charge');
    }
  };

  return (
    <div className="space-y-4 p-2">
      <Card>
        <CardHeader>
          <CardTitle>Unacceptable Charges</CardTitle>
          <CardDescription>
            Manage charges that automatically exclude referrals from intake acceptance.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-3">
            <div className="space-y-1">
              <Label htmlFor="charge-input">Charge</Label>
              <Input
                id="charge-input"
                value={chargeInput}
                onChange={(event) => setChargeInput(event.target.value)}
                placeholder="Enter charge text"
              />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label htmlFor="note-input">Note</Label>
              <Input
                id="note-input"
                value={noteInput}
                onChange={(event) => setNoteInput(event.target.value)}
                placeholder="Optional note"
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">{rows.length} active charges</p>
            <Button type="button" onClick={handleAdd} disabled={adding}>
              {adding ? 'Saving...' : 'Add Charge'}
            </Button>
          </div>

          <div className="max-h-[60vh] overflow-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Charge</TableHead>
                  <TableHead>Note</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!loading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-sm text-gray-500">
                      No unacceptable charges configured.
                    </TableCell>
                  </TableRow>
                )}

                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.id}</TableCell>
                    <TableCell className="font-medium">{row.charge}</TableCell>
                    <TableCell>{row.note || '-'}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        disabled={removing}
                        onClick={() => handleRemove(row.id)}
                      >
                        Remove
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReferralUnacceptableChargesPage;
